import type { Circuit, JokerLapBranch } from '../types/track.js';
import { TrackCellType } from '../types/track.js';
import { areCellsAdjacent } from './circuitGeometryEdit.js';
import { findPathIndexAt, isPathClosed } from './circuitEdit.js';
import { applyJokerLapToGrid } from './jokerLap.js';
import { createEmptyJokerLapBranch } from './jokerLap.js';

function isWithinGrid(circuit: Circuit, x: number, y: number): boolean {
  return x >= 0 && x < circuit.width && y >= 0 && y < circuit.height;
}

/** Case déjà occupée par le circuit principal (hors case de fermeture dupliquée). */
export function isCellOnMainPath(circuit: Circuit, x: number, y: number): boolean {
  const path = circuit.path;
  const end = isPathClosed(path) ? path.length - 1 : path.length;
  for (let i = 0; i < end; i++) {
    if (path[i].x === x && path[i].y === y) return true;
  }
  return false;
}

function neighborCoords(x: number, y: number): { x: number; y: number }[] {
  return [
    { x: x + 1, y },
    { x: x - 1, y },
    { x, y: y + 1 },
    { x, y: y - 1 },
  ];
}

function filterFreeJokerNeighbors(
  circuit: Circuit,
  anchorX: number,
  anchorY: number,
  jl: JokerLapBranch
): { x: number; y: number }[] {
  return neighborCoords(anchorX, anchorY).filter((cell) => {
    if (!isWithinGrid(circuit, cell.x, cell.y)) return false;
    if (isCellOnMainPath(circuit, cell.x, cell.y)) return false;
    if (jl.cells.some((c) => c.x === cell.x && c.y === cell.y)) return false;
    return true;
  });
}

export function ensureJokerLapDraft(circuit: Circuit): JokerLapBranch {
  if (!circuit.jokerLap) {
    circuit.jokerLap = createEmptyJokerLapBranch();
  }
  return circuit.jokerLap;
}

export function setJokerLapEntry(circuit: Circuit, pathIndex: number): Circuit {
  const jl = ensureJokerLapDraft(circuit);
  return {
    ...circuit,
    jokerLap: {
      ...jl,
      entryPathIndex: pathIndex,
      exitPathIndex: -1,
      cells: [],
    },
  };
}

export function tryAppendJokerLapCell(
  circuit: Circuit,
  x: number,
  y: number
): { circuit: Circuit } | { error: string } {
  const jl = circuit.jokerLap;
  if (!jl || jl.entryPathIndex < 0) {
    return { error: 'Choisissez d’abord l’entrée sur le circuit principal' };
  }

  const entry = circuit.path[jl.entryPathIndex];
  const cells = jl.cells;

  if (isCellOnMainPath(circuit, x, y)) {
    return { error: 'Cette case est déjà occupée par le circuit principal' };
  }

  if (cells.length === 0) {
    if (!areCellsAdjacent(entry.x, entry.y, x, y)) {
      return { error: 'La première case doit être adjacente à l’entrée' };
    }
    return {
      circuit: {
        ...circuit,
        jokerLap: {
          ...jl,
          cells: [{ x, y, type: TrackCellType.JOKER_LAP }],
        },
        grid: applyJokerLapToGrid({
          ...circuit,
          jokerLap: {
            ...jl,
            cells: [{ x, y, type: TrackCellType.JOKER_LAP }],
          },
        }),
      },
    };
  }

  const last = cells[cells.length - 1];
  if (last.x === x && last.y === y) {
    return { error: 'Case déjà sur le tour joker' };
  }
  if (!areCellsAdjacent(last.x, last.y, x, y)) {
    return { error: 'La case doit être adjacente à la précédente' };
  }

  const nextCells = [...cells, { x, y, type: TrackCellType.JOKER_LAP }];
  return {
    circuit: {
      ...circuit,
      jokerLap: { ...jl, cells: nextCells },
      grid: applyJokerLapToGrid({ ...circuit, jokerLap: { ...jl, cells: nextCells } }),
    },
  };
}

export function setJokerLapExit(
  circuit: Circuit,
  pathIndex: number
): { circuit: Circuit } | { error: string } {
  const jl = circuit.jokerLap;
  if (!jl || jl.entryPathIndex < 0) {
    return { error: 'Choisissez d’abord l’entrée' };
  }
  if (jl.cells.length === 0) {
    return { error: 'Tracez au moins une case pour le tour joker' };
  }
  if (pathIndex === jl.entryPathIndex) {
    return { error: 'La sortie doit être différente de l’entrée' };
  }

  const last = jl.cells[jl.cells.length - 1];
  const exitCell = circuit.path[pathIndex];
  if (!areCellsAdjacent(last.x, last.y, exitCell.x, exitCell.y)) {
    return { error: 'La sortie doit être adjacente à la dernière case du tour joker' };
  }

  return {
    circuit: {
      ...circuit,
      jokerLap: { ...jl, exitPathIndex: pathIndex },
    },
  };
}

export function undoLastJokerLapCell(circuit: Circuit): Circuit | null {
  const jl = circuit.jokerLap;
  if (!jl || jl.cells.length === 0) return null;
  const nextCells = jl.cells.slice(0, -1);
  const nextJl = { ...jl, cells: nextCells, exitPathIndex: -1 };
  return {
    ...circuit,
    jokerLap: nextJl,
    grid: applyJokerLapToGrid({ ...circuit, jokerLap: nextJl }),
  };
}

export function clearJokerLap(circuit: Circuit): Circuit {
  const grid = circuit.grid.map((row) =>
    row.map((cell) => (cell === TrackCellType.JOKER_LAP ? TrackCellType.EMPTY : cell))
  );
  return { ...circuit, jokerLap: undefined, grid };
}

export function jokerLapHitTest(
  circuit: Circuit,
  x: number,
  y: number
): 'entry' | 'branch' | 'exit' | null {
  const jl = circuit.jokerLap;
  if (!jl) return null;
  const mainIdx = findPathIndexAt(circuit, x, y);
  if (mainIdx === jl.entryPathIndex) return 'entry';
  if (jl.exitPathIndex >= 0 && mainIdx === jl.exitPathIndex) return 'exit';
  if (jl.cells.some((c) => c.x === x && c.y === y)) return 'branch';
  return null;
}

export function getJokerLapPlaceableCells(circuit: Circuit): { x: number; y: number }[] {
  const jl = circuit.jokerLap;
  if (!jl || jl.entryPathIndex < 0 || jl.exitPathIndex >= 0) return [];

  if (jl.cells.length === 0) {
    const entry = circuit.path[jl.entryPathIndex];
    return filterFreeJokerNeighbors(circuit, entry.x, entry.y, jl);
  }

  const last = jl.cells[jl.cells.length - 1];
  return filterFreeJokerNeighbors(circuit, last.x, last.y, jl);
}
